import { ButtonTypeAction, PageAttributeProps } from '../shared/modals/multi-page-modal/types';
import { useFocusImport } from './use-focus-import';
import { JsonFileImporter } from '../shared/file-importer/json-file-importer';
import { useFocusFileValidator } from '../shared/file-importer/validators/use-focus-file-validator';
import { focusMapper } from '../shared/file-importer/mappers/focus-json-mapper';
import { Typography } from '@SBGSports/referee-react';
import { useTranslations } from 'i18n';
import { useGetDeepActivity } from 'api';
import { useEffect, useRef, useState } from 'react';
import { RadioButton } from '../reporting/components/radio-button/radio-button';
import styles from './focus-import.module.css';
import { FocusFile } from '../shared/file-importer/types';
import { ATHLETE_OPTION, TACTICAL_OPTION } from './constants';
import { useFocusStoredOptions } from './use-focus-stored-options';
import { useMultiPageWizard } from '../shared/modals/multi-page-modal/use-multi-page-wizard';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { AnnotationLayerResponse, useDeleteAnnotationLayer, useGetAnnotationLayers } from 'api';
import { ImportFeedback } from './component/import-feedback';

export const StartupPageModal: React.FC<PageAttributeProps> = (props) => {
    const { setPathOption, setButtonDetails } = props;
    const { activityId, setFocusFile, focusFile } = useFocusImport();
    const { setHeader } = useMultiPageWizard();
    const { data } = useGetDeepActivity(activityId);
    const validator = useFocusFileValidator();
    const { __ } = useTranslations();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();

    const { data: annotationsLayers } = useGetAnnotationLayers(activityId);
    const { mutateAsync: deleteAnnotationLayer } = useDeleteAnnotationLayer();
    const { next } = useMultiPageWizard();

    //States
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [isDuplicateFile, setIsDuplicateFile] = useState<boolean>(false);
    const [isDeleteError, setIsDeleteError] = useState<boolean>(false);

    //Track the time on page analytics
    const mixpanelRefExecuted = useRef(false);
    useEffect(() => {
        if (!mixpanelRefExecuted.current) {
            mixpanel.track(EVENTS.focus.startUpPageModalStarted, {});
            mixpanel.time_event(EVENTS.focus.startUpPageModalEnded);
            return () => {
                mixpanel.track(EVENTS.focus.startUpPageModalEnded, { hasFocusFile: !!focusFile });
            };
        }
        mixpanelRefExecuted.current = true;
    }, [focusFile]);

    /**
     * Set header and custom button details here on first render to display on parent component
     */
    const executedRef = useRef(false);
    useEffect(() => {
        if (executedRef.current) {
            return;
        }

        setButtonDetails?.([
            {
                type: ButtonTypeAction.advance,
                text: __('misc.next'),
                isDisabled: !focusFile,
                action: async () => {
                    if (isDuplicateFile) {
                        try {
                            const annotationLayerId =
                                annotationsLayers?.find(
                                    (annotation) => annotation.name === focusFile?.session.sessionId,
                                )?.id ?? '';
                            await deleteAnnotationLayer({ annotationLayerId }).then(() => {
                                next();
                            });
                        } catch (error) {
                            setIsDeleteError(!!error);
                        }
                    } else {
                        next();
                    }
                },
            },
        ]);
        setHeader(
            <Typography variant="heading-2">{__('smt_importer.import_heading', { productName: 'Focus' })}</Typography>,
        );
        executedRef.current = true;
    }, [__, annotationsLayers, deleteAnnotationLayer, focusFile, isDuplicateFile, next, setButtonDetails, setHeader]);

    const isExistingAnnotation = (annotationLayers: AnnotationLayerResponse[] | undefined, name: string) => {
        return annotationLayers?.some((annotation) => annotation.name === name);
    };

    return (
        <div data-testid="focus-start-page" className={styles['start-page-body']}>
            <Typography variant="body-1" className={styles['file-import-description']}>
                {__('smt_importer.import_file_description', {
                    productName: 'Focus',
                    fileType: '.JSON',
                    activityName: data?.[0]?.name ?? '',
                })}
            </Typography>
            <div className={styles['import-input-wrapper']}>
                <JsonFileImporter
                    fileValidator={validator}
                    json_mapper={focusMapper}
                    setData={(parsedData: FocusFile | undefined, selectedFileName: string) => {
                        const importOption = focusStoredOptions.startImportOption ?? TACTICAL_OPTION;
                        if (!focusFile && !parsedData) {
                            mixpanel.track(EVENTS.focus.startUpPageModal, { validFocusFile: false });
                            setFocusFile(undefined);
                        } else {
                            setFocusFile(parsedData);
                            setPathOption?.(importOption);
                            mixpanel.track(EVENTS.focus.startUpPageModal, {
                                validFocusFile: true,
                                importOption: importOption,
                            });
                            executedRef.current = false;
                        }

                        setFocusStoredOptions({
                            ...focusStoredOptions,
                            isBaking: false,
                            startImportOption: importOption,
                        });
                        setFileName(selectedFileName);
                        setIsDirty(!!selectedFileName);
                        setIsDeleteError(false);

                        if (isExistingAnnotation(annotationsLayers, parsedData?.session?.sessionId ?? '')) {
                            setIsDuplicateFile(true);
                        } else {
                            setIsDuplicateFile(false);
                        }
                    }}
                />
                <ImportFeedback
                    fileName={fileName}
                    focusFile={focusFile}
                    isDuplicateFile={isDuplicateFile}
                    showDeleteError={isDeleteError}
                    showValidationFeedback={!!focusFile || isDirty}
                />
            </div>
            {focusFile && (
                <>
                    <div>
                        <Typography variant="body-1">{__('smt_importer.initial_import_prompt')}</Typography>
                    </div>
                    <div className={styles['radio-flex']}>
                        <RadioButton
                            label={__('smt_importer.focus_tactical_option_label')}
                            onChange={(option: string) => {
                                setFocusStoredOptions({
                                    ...focusStoredOptions,
                                    startImportOption: option as typeof TACTICAL_OPTION,
                                });
                                setPathOption?.(TACTICAL_OPTION);
                                mixpanel.track(EVENTS.focus.startUpPageModalImportOptionChanged, {
                                    optionSelected: TACTICAL_OPTION,
                                });
                            }}
                            value={TACTICAL_OPTION}
                            isChecked={focusStoredOptions.startImportOption === TACTICAL_OPTION}
                            name={'annotation_focus'}
                            disabled={!focusFile}
                        />
                        <RadioButton
                            label={__('smt_importer.focus_athlete_option_label')}
                            onChange={(option: string) => {
                                setFocusStoredOptions({
                                    ...focusStoredOptions,
                                    startImportOption: option as typeof ATHLETE_OPTION,
                                });
                                setPathOption?.(ATHLETE_OPTION);
                                mixpanel.track(EVENTS.focus.startUpPageModalImportOptionChanged, {
                                    optionSelected: ATHLETE_OPTION,
                                });
                            }}
                            value={ATHLETE_OPTION}
                            isChecked={focusStoredOptions.startImportOption === ATHLETE_OPTION}
                            name={'annotation_focus'}
                            disabled={!focusFile}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
